import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import { mdiCheckCircleOutline, mdiFileQuestionOutline, mdiClipboardAlertOutline } from '@mdi/js'
import type { ImportReview } from './importTypes'

interface ImportReviewCardProps {
    org: string
    importUid: string
    review: ImportReview
}

/**
 * The "To review" card (spec 2026-08-16-07 §2): the two numbers that say the
 * deposit still needs a human. Each row links to the list filtered accordingly
 * — unclassified documents to the documents list, extraction gaps to the gaps
 * panel further down this same page, which already carries the per-rule
 * drill-downs.
 */
export function ImportReviewCard({ org, importUid, review }: ImportReviewCardProps) {
    const { t } = useTranslation('imports')

    const clean = review.unclassified === 0 && review.extraction_gaps === 0

    return (
        <section data-testid="import-review" className="space-y-3">
            <h2 className="text-sm font-medium text-muted-foreground">{t('review.title')}</h2>

            {clean ? (
                <p
                    className="flex items-center gap-2 rounded-lg border border-success/25 bg-success/10 p-4 text-sm text-foreground"
                    data-testid="import-review-clean"
                >
                    <AppIcon path={mdiCheckCircleOutline} size="md" className="shrink-0 text-success" />
                    {t('review.clean')}
                </p>
            ) : (
                <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <li>
                        <Link
                            to="/$org/documents"
                            params={{ org }}
                            search={{
                                asset_uid: undefined,
                                document_type_uid: undefined,
                                flags: undefined,
                                import_uid: importUid,
                                unclassified: true,
                                limit: 100,
                            }}
                            data-testid="import-review-unclassified"
                            className="flex h-full items-center gap-3 rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/40"
                        >
                            <AppIcon tone="muted" path={mdiFileQuestionOutline} size="lg" className="shrink-0" />
                            <div className="min-w-0">
                                <p
                                    className="text-2xl font-bold text-foreground"
                                    data-testid="import-review-unclassified-value"
                                >
                                    {review.unclassified}
                                </p>
                                <p className="text-sm text-foreground">{t('review.unclassified')}</p>
                                <p className="text-xs text-muted-foreground">{t('review.unclassified_hint')}</p>
                            </div>
                        </Link>
                    </li>

                    <li>
                        {/*
                          Gaps have no list of their own: each rule carries its
                          own drill-down on the gaps panel below, so the card
                          jumps there rather than inventing a filter.
                        */}
                        <a
                            href="#import-gaps"
                            data-testid="import-review-gaps"
                            className="flex h-full items-center gap-3 rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/40"
                        >
                            <AppIcon tone="muted" path={mdiClipboardAlertOutline} size="lg" className="shrink-0" />
                            <div className="min-w-0">
                                <p
                                    className="text-2xl font-bold text-foreground"
                                    data-testid="import-review-gaps-value"
                                >
                                    {review.extraction_gaps}
                                </p>
                                <p className="text-sm text-foreground">{t('review.extraction_gaps')}</p>
                                <p className="text-xs text-muted-foreground">
                                    {t('review.extraction_gaps_hint')}
                                </p>
                            </div>
                        </a>
                    </li>
                </ul>
            )}
        </section>
    )
}
